import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api';

/**
 * How the application retries, and when it refuses to.
 *
 * The default -- three retries with backoff, on everything -- is wrong for this
 * API. A 403 will be a 403 again, a 404 will still be missing, and a 422 will
 * be rejected identically: retrying those turns an instant, accurate refusal
 * into several seconds of spinner followed by the same refusal. So retries are
 * limited to the two failures that a second attempt can actually fix: a request
 * that never arrived, and a server that fell over.
 *
 * The cache holds whatever the screens have read, which on later screens
 * includes personal data. It lives in memory, like the session, and is emptied
 * the moment a session ends (see `App.tsx`) so that signing in as someone else
 * cannot show the previous person's data even for one frame.
 */
const MAX_RETRIES = 2;

/**
 * Throw away every answer the application is holding, because "now" moved.
 *
 * Used by the simulated-clock control in the header. Nothing else in the app
 * invalidates this broadly, and nothing else should: a normal write knows which
 * keys it touched, and a blanket invalidation there would refetch a dozen
 * screens to update one. Moving the clock is the one change that touches
 * everything -- an as-of date, a cutoff, an overdue flag and a competence are
 * all computed against the current instant, so every cached answer was
 * computed against an instant that no longer applies. Enumerating the keys that
 * "depend on now" would be enumerating all of them, and getting it wrong would
 * leave a screen quietly showing yesterday's arithmetic.
 *
 * Deliberately `invalidateQueries` rather than `clear`: emptying the cache
 * unmounts data the screens are rendering and blanks them to spinners, while
 * invalidating refetches under them and swaps the numbers when the answers
 * arrive.
 */
export async function invalidateEverything(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries();
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (failureCount >= MAX_RETRIES) {
            return false;
          }
          return error instanceof ApiError ? error.isRetryable : false;
        },
        staleTime: 30_000,
        // Refetching on every window focus is noise on a data-entry screen and
        // a surprise on a screen someone is reading from.
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
